import { useState, useEffect, useCallback } from 'react';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';

export interface AgendaItem {
  id: string;
  partner_id: string;
  lead_id: string | null;
  type: 'showroom' | 'werkbezoek' | 'installatie' | 'opmeetbezoek' | 'geblokkeerd' | 'anders';
  name: string;
  date: string;
  time: string;
  notes: string;
  end_date?: string;
  all_day?: boolean;
  color?: string;
  google_event_id?: string;
}

const VALID_TYPES = ['showroom', 'werkbezoek', 'installatie', 'opmeetbezoek', 'geblokkeerd'];

const TYPE_LABELS: Record<string, string> = {
  showroom: 'Showroom bezoek',
  werkbezoek: 'Werkbezoek',
  installatie: 'Installatie',
  opmeetbezoek: 'Opmeetbezoek',
  geblokkeerd: 'Geblokkeerd',
  anders: 'Afspraak',
};

async function fireSyncEvent(payload: {
  operation: 'create' | 'delete';
  agenda_item_id?: string;
  title?: string;
  date?: string;
  time?: string;
  notes?: string;
  google_event_id?: string;
}) {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session?.access_token) return;
    const url = `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/sync_calendar_event`;
    await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${session.access_token}` },
      body: JSON.stringify(payload),
    });
  } catch (err) {
    console.warn('[useAgenda] sync_calendar_event failed:', err);
  }
}

const MOCK_ITEMS: AgendaItem[] = [
  { id: 'a1', partner_id: 'partner-003', lead_id: null, type: 'showroom',     name: 'Jan de Vries',   date: '2026-05-11', time: '10:00', notes: '' },
  { id: 'a2', partner_id: 'partner-003', lead_id: null, type: 'opmeetbezoek', name: 'Lisa Bakker',    date: '2026-05-12', time: '14:30', notes: 'Opmeting keuken + woonkamer', color: '#A89880' },
  { id: 'a3', partner_id: 'partner-003', lead_id: null, type: 'installatie',  name: 'Marco Jansen',  date: '2026-05-18', time: '', notes: 'Houtlook Premium PVC, 34m²', end_date: '2026-05-22', all_day: true, color: '#7A8C6E' },
  { id: 'a4', partner_id: 'partner-003', lead_id: null, type: 'showroom',     name: 'Sophie Brouwer', date: '2026-05-13', time: '11:30', notes: '' },
  { id: 'a5', partner_id: 'partner-003', lead_id: null, type: 'installatie',  name: 'Thomas Visser', date: '2026-05-25', time: '', notes: 'Visgraat Premium PVC, 52m²', end_date: '2026-05-29', all_day: true, color: '#7A8C6E' },
  { id: 'a6', partner_id: 'partner-003', lead_id: null, type: 'opmeetbezoek', name: 'Emma van Dijk',  date: '2026-05-21', time: '13:00', notes: '', color: '#A89880' },
  { id: 'a7', partner_id: 'partner-003', lead_id: null, type: 'anders',       name: 'Peter Smit',    date: '2026-05-28', time: '10:30', notes: 'Evaluatiegesprek na oplevering' },
];

function mapItem(row: any): AgendaItem {
  return {
    id: row.id,
    partner_id: row.partner_id,
    lead_id: row.lead_id ?? null,
    type: (VALID_TYPES.includes(row.type) ? row.type : 'anders') as AgendaItem['type'],
    name: row.customer_name || row.label || row.name || '',
    date: row.date || '',
    time: row.time || '',
    notes: row.notes || '',
    end_date: row.end_date || undefined,
    all_day: row.all_day || false,
    color: row.color || undefined,
    google_event_id: row.google_event_id ?? undefined,
  };
}

export function useAgenda(partnerId: string | null, leadId?: string | null) {
  const [items, setItems] = useState<AgendaItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!partnerId) { setLoading(false); return; }

    if (!isSupabaseConfigured) {
      let mock = MOCK_ITEMS.filter(i => i.partner_id === partnerId);
      if (leadId !== undefined) mock = mock.filter(i => i.lead_id === leadId);
      setItems(mock);
      setLoading(false);
      return;
    }

    const fetch = async () => {
      setLoading(true);
      try {
        let q = supabase.from('agenda_items').select('*').eq('partner_id', partnerId).order('date').order('time');
        if (leadId !== undefined) q = (q as any).eq('lead_id', leadId);
        const { data } = await q;
        setItems((data || []).map(mapItem));
      } catch {
        let mock = MOCK_ITEMS.filter(i => i.partner_id === partnerId);
        if (leadId !== undefined) mock = mock.filter(i => i.lead_id === leadId);
        setItems(mock);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [partnerId, leadId]);

  const addItem = useCallback(async (data: Omit<AgendaItem, 'id'>): Promise<string> => {
    const tempId = `a-${Date.now()}`;
    const newItem: AgendaItem = { ...data, id: tempId };
    setItems(prev => [...prev, newItem].sort((a, b) => (a.date + a.time).localeCompare(b.date + b.time)));
    if (!isSupabaseConfigured) return tempId;

    const { partner_id, lead_id, type, date, time, notes, name, end_date, all_day, color } = data;
    const { data: row } = await supabase.from('agenda_items').insert({
      partner_id, lead_id, type, date, time, notes,
      customer_name: name,
      end_date: end_date || null,
      all_day: all_day || false,
      color: color || null,
    }).select().single();

    if (row) {
      const mapped = mapItem(row);
      setItems(prev => prev.map(i => i.id === tempId ? mapped : i));
      if (data.type !== 'geblokkeerd') {
        fireSyncEvent({
          operation: 'create',
          agenda_item_id: row.id,
          title: `${TYPE_LABELS[data.type] || data.type} — ${data.name}`,
          date: data.date,
          time: data.time,
          notes: data.notes,
        });
      }
      return row.id;
    }
    return tempId;
  }, []);

  const updateItem = useCallback(async (id: string, patch: Partial<Omit<AgendaItem, 'id'>>) => {
    setItems(prev => prev.map(i => i.id === id ? { ...i, ...patch } : i));
    if (!isSupabaseConfigured) return;
    const dbPatch: Record<string, unknown> = {};
    if (patch.type !== undefined) dbPatch.type = patch.type;
    if (patch.name !== undefined) dbPatch.customer_name = patch.name;
    if (patch.date !== undefined) dbPatch.date = patch.date;
    if (patch.time !== undefined) dbPatch.time = patch.time;
    if (patch.notes !== undefined) dbPatch.notes = patch.notes;
    if (patch.end_date !== undefined) dbPatch.end_date = patch.end_date;
    if (patch.all_day !== undefined) dbPatch.all_day = patch.all_day;
    if (patch.color !== undefined) dbPatch.color = patch.color;
    await supabase.from('agenda_items').update(dbPatch).eq('id', id);
  }, []);

  const deleteItem = useCallback(async (id: string) => {
    setItems(prev => prev.filter(i => i.id !== id));
    if (!isSupabaseConfigured) return;
    const { data: existing } = await supabase
      .from('agenda_items').select('google_event_id').eq('id', id).single();
    await supabase.from('agenda_items').delete().eq('id', id);
    if (existing?.google_event_id) {
      fireSyncEvent({ operation: 'delete', google_event_id: existing.google_event_id });
    }
  }, []);

  return { items, loading, addItem, updateItem, deleteItem };
}
